/** @module SiteAdmin/DocumentTable */
define([
	"dojo/_base/declare",
	"csi/_base/modules",
	"csi/access/crews",
	"csi/core/Key",
	"csi/def/Heading",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/sessionConstants",
	"csi/state/viewUtils",
	"csi/store/Values",
	"csi/table/Widget",
	"csi/xml",
	"csi/xml/Query"
], function(declare, modules, crews, Key, Heading, reflection, reflectionHints, sessionConstants, {decomposeViewKey}, Values, TableWidget, xml, Query) {

	/**
	 * Determine if user is allowed to add documents to given node
	 * @param {Node}
	 * @return {boolean}
	 */
	function _canAddDocuments(rootNode) {
		const rc = reflection.getClass("Document");

		if (!rc?.enabled) {
			return false;
		}

		// only allow access to create site level document when user has Site Document Admin access, see C8-20440
		if (rootNode?.__Parent__ == "centric://REFLECTION/INSTANCE/DocumentGroup/Processes") {
			return crews.isInRoles(["Site Document Administrator"], rootNode);
		}

		return xml.nodeHasRight(rootNode, xml.RIGHT_WRITE);
	}

	return declare(TableWidget, {

		/**
		 * A Document can be viewed if its subtype is viewable or it has variants
		 * @param {Context} ctx
		 * @param {Action} action
		 */
		accessAction3D(ctx, action) {
			const revision = ctx.getNode();
			const document = ctx.getNodeParent();
			return (!!revision.Variants.length && !!revision.AlternateFiles.length) || reflection.getSubtypeSetting(document.Subtype, "Is3DViewable");
		},

		/**
		 * @override
		 * Verify the availability of the DocumentGenerator module
		 * Ensures that the "NewfromDocumentTemplate" action is displayed only if the "PrimaryType"
		 * is associated with the parent type
		 */
		onViewStart(dfds) {
			const {cacheView, relRoot} = this;

			if (modules.hasModule("DocumentGenerator") && relRoot.m_news) {
				const node = this.getViewNode();
				const parent = cacheView.get(node?.__Parent__);
				const parentType = parent?.$Type;

				const q = new Query();
				q.Attr("Active", xml.OP_EQUALS, true);
				q.Attr("State", xml.OP_EQUALS, "Revision.State:APPROVED", "Child:CurrentRevision");

				const store = new Values({
					filterQuery: q,
					requestParameters: {
						"Qry.Value.Attr": "PrimaryType"
					},
					typeNode: "DGaaSTemplate",
				});

				const dfd = store.query().then((results) => {
					const dGaaSTemplateTypes = results.map((result) => result[store.idProperty]);
					if (!dGaaSTemplateTypes?.includes(parentType)) {
						relRoot.m_news = relRoot.m_news.filter((a) => a.m_id != "NewFromDocumentTemplate")
					}
				});
				dfds.push(dfd);
			}
		},

		/**
		 * Separate custom views for supplier documents view
		 */
		_resolveView() {
			const {cacheView, customViewMigrationContainerKey, defView, relRoot} = this;
			const headings = defView.m_headings;
			const node = this.getViewNode();

			if (node && !_canAddDocuments(node)) {
				relRoot.m_news = null;
			}

			if (!headings?.length) {
				const context = cacheView.get(node?.__Parent__);
				const contextType = context?.$Type;
				const migrationContextType = customViewMigrationContainerKey ? decomposeViewKey(customViewMigrationContainerKey).context : null;

				if ((contextType === "Project") || (migrationContextType === "Project")) {
					// add the AppliesToProjectLineItems only for Project and set the prefKey
					defView.m_headings = [
						Object.assign(new Heading(), {m_id: "Document.Default", m_bCard: true}),
						Object.assign(new Heading(), {m_id: "AppliesToProjectLineItems"})
					];

					this.prefKey = defView.getKey("Project");
				} else if (sessionConstants.allow3d && ((contextType === "Style") || (migrationContextType === "ColorVariants"))) {
					// add variant matrix if desired and separate custom views
					defView.m_headings = [
						Object.assign(new Heading(), {m_id: "Document.Default", m_bCard: true}),
						Object.assign(new Heading(), {m_id: "Document.ColorVariants", m_bCard: true})
					];

					this.prefKey = defView.getKey("ColorVariants");
				}
			}

			this.inherited(arguments);
		},

		/**
		 * Only allow access to push document if container allows referenced documents and user is not a Supplier user
		 * @param {Context} ctx
		 * @param {Action} action
		 * @return {boolean}
		 */
		onAccessActionPushDocument(ctx, action) {
			if (sessionConstants.userType == "Supplier") {
				return false;
			}

			var rootNode = ctx.getNodeParent(ctx.getParent());
			if (!rootNode) {
				return false;
			}

			// allow sharing documents from home level
			if (rootNode.$Type == "DocumentGroup") {
				return true;
			}

			return reflectionHints.isReferenceDocumentOwner(rootNode.$Type);
		},

		/**
		 * @override
		 * Only show Document.SharedWith heading on Shape and Theme, skip over otherwise
		 */
		onViewResolveKeysToSkip(skipSet) {
			const viewNode = this.getViewNode();
			const docAndCommentOwner = this.cacheView.get(viewNode?.__Parent__);
			const ownerType = docAndCommentOwner?.$Type;

			if (ownerType && (ownerType !== "Shape" && ownerType !== "Theme")) {
				skipSet.add(Key.untypedKeyFromParts({
					id: "SharedWith",
				}));
			}
		},
	});
});
